import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Loader2, MonitorPlay, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminLang } from '../../hooks/useAdminLang';

/**
 * FilmsLibresToggle — Admin controls for the "Films libre de droit" section:
 *  - visibility (show / hide in Découvrir)
 *  - playback mode (in-app embedded player / external web browser)
 */
export default function FilmsLibresToggle() {
  const { at } = useAdminLang();
  const [visible, setVisible] = useState(true);
  const [mode, setMode] = useState('inapp');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingMode, setSavingMode] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get('/api/settings/films-libres-visibility').then(r => setVisible(!!r.data?.visible)).catch(() => {}),
      axios.get('/api/settings/films-libres-playback').then(r => setMode(r.data?.mode === 'browser' ? 'browser' : 'inapp')).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      const r = await axios.post('/api/admin-ext/films-libres-visibility', { visible: !visible });
      setVisible(!!r.data?.visible);
      toast.success(r.data?.visible ? at('filmsLibresOk') : at('filmsLibresKo'));
    } catch {
      toast.error(at('filmsLibresError'));
    } finally { setSaving(false); }
  };

  const setPlayback = async (next) => {
    if (next === mode || savingMode) return;
    setSavingMode(true);
    try {
      const r = await axios.post('/api/admin-ext/films-libres-playback', { mode: next });
      setMode(r.data?.mode || next);
      toast.success(next === 'inapp' ? at('filmsPlaybackInAppOk') : at('filmsPlaybackBrowserOk'));
    } catch {
      toast.error(at('filmsLibresError'));
    } finally { setSavingMode(false); }
  };

  if (loading) return <div className="p-4"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="mb-4 space-y-3">
      <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30" data-testid="films-libres-toggle">
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-emerald-200 text-sm">{at('filmsLibresTitle')}</h4>
            <p className="text-[11px] text-emerald-200/70 mt-0.5 leading-snug">{at('filmsLibresDesc')}</p>
          </div>
          <button
            onClick={toggle}
            disabled={saving}
            className={`shrink-0 px-4 py-2 rounded-full font-semibold text-xs flex items-center gap-2 ${
              visible ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-white/10 hover:bg-white/20 text-white'
            } disabled:opacity-50`}
            data-testid="films-libres-toggle-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />)}
            {visible ? at('filmsLibresVisible') : at('filmsLibresHidden')}
          </button>
        </div>
      </div>

      <div className="p-4 rounded-2xl bg-gradient-to-br from-red-500/10 to-rose-500/10 border border-red-500/30" data-testid="films-libres-playback">
        <h4 className="font-bold text-red-200 text-sm">{at('filmsPlaybackTitle')}</h4>
        <p className="text-[11px] text-red-200/70 mt-0.5 mb-3 leading-snug">{at('filmsPlaybackDesc')}</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setPlayback('inapp')}
            disabled={savingMode}
            className={`px-3 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 border transition-colors ${
              mode === 'inapp' ? 'bg-red-500 border-red-400 text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
            } disabled:opacity-50`}
            data-testid="films-playback-inapp-btn"
          >
            <MonitorPlay className="w-3.5 h-3.5" /> {at('filmsPlaybackInApp')}
          </button>
          <button
            onClick={() => setPlayback('browser')}
            disabled={savingMode}
            className={`px-3 py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 border transition-colors ${
              mode === 'browser' ? 'bg-red-500 border-red-400 text-white' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
            } disabled:opacity-50`}
            data-testid="films-playback-browser-btn"
          >
            <Globe className="w-3.5 h-3.5" /> {at('filmsPlaybackBrowser')}
          </button>
        </div>
      </div>
    </div>
  );
}
